import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Badge, CTA, HtmlRaw, Text } from '../../../ui/atoms';
import {
  Wrapper,
  CourseListContainer,
  PageGrid,
  Title,
  CategoryCard,
  CategoriesWrapper,
  PageWrapper,
} from './style';
import {
  CATALOGS_GET,
  COURSE_LIST_RESET,
  GLOBAL_COURSE_CATEGORIES_GET,
} from '../../redux/actions/courselist';
import { Container } from '../../../ui/gridSystem';
import { withMediaQueries } from '../../hoc/withMediaQueries';
import { StickyFiltersWrapper } from '../../../ui/atoms/CTA/style';
import { MODAL_OPEN } from '../../redux/actions';
import ModalCategories, {
  ModalCategoriesId,
} from '../../../ui/atoms/Modals/ModalCategories';
import Tabs from '../../../ui/atoms/Tabs';
import ModuleResults from './ModuleResults';
import CourseResults from './CourseResults';
import { REGION_PROVINCE_LIST_GET } from '../../redux/actions/user';
import storage from '../../api/storage';
import ModalSelectExport, {
  ModalSelectExportId,
} from '../../../ui/atoms/Modals/ModalSelectExport';
import routes from '../../../routes';

const defaultTabs = [
  { value: 'courses', label: 'Corsi' },
  { value: 'modules', label: 'Moduli' },
];

export const CATALOGS_OPTS = [
  {
    label: 'Catalogo Nazionale',
    value: 'CN',
  },
  {
    label: 'Catalogo Locale',
    value: 'CL',
  },
];

const FullCatalog = ({
  mediaIsPhone,
  courseListReset,
  modalOpen,
  isSupervisor,
  coursePager,
  modulePager,
  catalogs,
  getCatalogs,
  categories,
  getCategories,
  getRegiorProvinceList,
}) => {
  const [currentArea, setCurrentArea] = useState({});
  const [currentCatalog, setCurrentCatalog] = useState({});
  // tabs
  const [tabs, setTabs] = useState(defaultTabs);
  const [activeTab, setActiveTab] = useState(defaultTabs[0]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (catalogs.length <= 0) getCatalogs();
    getCategories();
    return () => {
      courseListReset();
      setCurrentArea({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newTabs = [...defaultTabs];
    newTabs[0].items = coursePager?.total_items ?? 0;
    newTabs[1].items = modulePager?.total_items ?? 0;
    setTabs(newTabs);
  }, [coursePager, modulePager]);

  const onClickCategory = (c) => {
    if (currentArea.id === c.id) setCurrentArea({});
    else setCurrentArea(c);
  };

  useEffect(() => {
    const catalog = catalogs?.find((c) => c.code === 'CN');
    setCurrentCatalog(catalog);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogs, isSupervisor]);

  useEffect(() => {
    if (isSupervisor) getRegiorProvinceList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupervisor]);

  useEffect(() => {
    storage.write('activeTab', activeTab?.value);
  }, [activeTab]);

  return (
    <>
      <Container>
        <Title>
          <Text type="h1" mb={mediaIsPhone ? 20 : 0}>
            Catalogo
          </Text>
          <CTA
            onClick={() =>
              isSupervisor
                ? modalOpen(ModalSelectExportId)
                : window.open(
                    `${window.location.origin}${routes.downloadCatalog.path}`,
                    '_blank'
                  )
            }
            btnText="Scarica il catalogo"
            type="tertiary"
            icon="download-01"
            bottomRight={!mediaIsPhone}
          />
        </Title>
        <Text type="b2" mb={40}>
          In questa pagina è possibile visualizzare, in base all’area tematica
          selezionata, la lista di tutti i corsi e moduli nazionali{' '}
          {isSupervisor && 'e locali '} presenti in piattaforma.
        </Text>
        <PageGrid>
          {mediaIsPhone ? (
            <StickyFiltersWrapper mb={16}>
              <CTA
                btnText="CAMBIA AREA TEMATICA"
                fullsize
                onClick={() =>
                  modalOpen(ModalCategoriesId, {
                    categories,
                  })
                }
              />
            </StickyFiltersWrapper>
          ) : (
            <div>
              <Text type="h2" className="category-title">
                Area tematica
              </Text>
              <CategoriesWrapper>
                {(categories || []).map((c) => {
                  if (!c.coursesByCategory) return null;
                  return (
                    <CategoryCard
                      key={`category-${c?.id}`}
                      onClick={() => onClickCategory(c)}
                      className={currentArea.id === c?.id ? 'active' : ''}
                    >
                      <Text type="b3" className="label">
                        {c.label}
                      </Text>
                      {c?.coursesByCategory && (
                        <Badge
                          type="counter"
                          active={currentArea.id === c?.id}
                          notification={c?.hasNewCourses}
                        >
                          {c.coursesByCategory}
                        </Badge>
                      )}
                    </CategoryCard>
                  );
                })}
              </CategoriesWrapper>
            </div>
          )}
          <CourseListContainer>
            <Wrapper>
              <Text type="h1.5" mb={16}>
                {currentArea.label ?? 'Tutti i corsi'}
              </Text>
              <Text type="b3" mb={48}>
                <HtmlRaw html={currentArea?.description || ''} />
              </Text>
              <Tabs
                tabs={tabs}
                selected={activeTab}
                onChange={(tab) => {
                  setActiveTab(tab);
                }}
              />
              <PageWrapper
                className={activeTab?.value === 'courses' ? 'active' : ''}
              >
                <CourseResults
                  currentCatalog={currentCatalog}
                  currentArea={currentArea}
                  active={activeTab?.value === 'courses'}
                />
              </PageWrapper>
              <PageWrapper
                className={activeTab?.value === 'modules' ? 'active' : ''}
              >
                <ModuleResults
                  currentCatalog={currentCatalog}
                  currentArea={currentArea}
                  active={activeTab?.value === 'modules'}
                />
              </PageWrapper>
            </Wrapper>
          </CourseListContainer>
        </PageGrid>
      </Container>
      {mediaIsPhone && (
        <ModalCategories
          onClick={(category = {}) => setCurrentArea(category)}
        />
      )}
      <ModalSelectExport />
    </>
  );
};

FullCatalog.propTypes = {
  mediaIsPhone: PropTypes.bool,
  // HOC (connect, state)
  isSupervisor: PropTypes.bool,
  coursePager: PropTypes.object.isRequired,
  modulePager: PropTypes.object.isRequired,
  categories: PropTypes.array,
  catalogs: PropTypes.array,
  // HOC (connect, dispatch)
  getCatalogs: PropTypes.func.isRequired,
  getCategories: PropTypes.func.isRequired,
  courseListReset: PropTypes.func.isRequired,
  modalOpen: PropTypes.func.isRequired,
  getRegiorProvinceList: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => {
      const {
        courses: { pager: coursePager },
        modules: { pager: modulePager },
        globalCategories: categories,
        catalogs,
      } = state.courselist;
      const { isSupervisor } = state.user;
      return {
        coursePager,
        modulePager,
        categories,
        catalogs,
        isSupervisor,
      };
    },
    (dispatch) => ({
      courseListReset: () => dispatch({ type: COURSE_LIST_RESET }),
      modalOpen: (id, payload) => dispatch({ type: MODAL_OPEN, id, payload }),
      getCatalogs: () => dispatch({ type: CATALOGS_GET._REQUEST }),
      getCategories: () =>
        dispatch({ type: GLOBAL_COURSE_CATEGORIES_GET._REQUEST }),
      getRegiorProvinceList: () =>
        dispatch({ type: REGION_PROVINCE_LIST_GET._REQUEST }),
    })
  ),
  withMediaQueries
)(FullCatalog);
